import { sanitizeIssueRichText } from '@supply-mover-2-0/shared';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

interface IssueRichTextContentProps {
	/** Sanitized rich text from the API (`descriptionHtml`, `noteHtml`, `bodyHtml`). */
	html?: string | null;
	/** The plain text, shown when the value has no rich text. */
	text?: string | null;
	/** Shown when there is neither. Omit to render nothing. */
	emptyText?: string;
	className?: string;
}

/**
 * PD-16475 — shows an issue description, note or storefront message. Rich
 * text is sanitized again in the browser with the same allow-list the server
 * applied, so a value that ever bypassed the server rule still renders as
 * nothing but formatting. Values stored as plain text (everything written
 * before rich text, or through a plain-text path) keep their line breaks.
 */
export function IssueRichTextContent({
	html,
	text,
	emptyText,
	className,
}: IssueRichTextContentProps): React.JSX.Element | null {
	const { t } = useTranslation();

	const safeHtml = useMemo(
		() => (html != null && html.length > 0 ? sanitizeIssueRichText(html) : ''),
		[html],
	);

	if (safeHtml.length > 0) {
		return (
			<div
				className={cn('rich-text-content break-words', className)}
				// Sanitized above by the shared allow-list (tags p, br, strong,
				// em, u, ul, ol, li, and vetted links only).
				dangerouslySetInnerHTML={{ __html: safeHtml }}
			/>
		);
	}

	const plain = text?.trim() ?? '';

	if (plain.length > 0) {
		return (
			<div className={cn('whitespace-pre-wrap break-words', className)}>
				{t(plain)}
			</div>
		);
	}

	return emptyText != null ? (
		<div className={className}>{t(emptyText)}</div>
	) : null;
}
